import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useWatch, useController } from "react-hook-form";
import { zodResolver } from "@/lib/zodcompat";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { invoiceSchema, type InvoiceFormData } from "@/validators/invoice";
import { useInvoice, useCreateInvoice, useUpdateInvoice } from "@/hooks/useInvoices";
import { useCompanies } from "@/hooks/useCompanies";
import { useFreights } from "@/hooks/useFreights";
import { FreightSelector } from "@/components/form/freight-selector";
import { FormSection } from "@/components/form/form-section";
import { MoneyInput } from "@/components/form/money-input";
import { ComboboxSelect } from "@/components/form/combobox-select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/loading-button";

function toDateInput(iso: string | null | undefined) {
  if (!iso) return "";
  return new Date(iso).toISOString().slice(0, 10);
}

function toISOString(dateInput: string) {
  if (!dateInput) return "";
  return new Date(dateInput).toISOString();
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function addDaysISO(baseDate: string, days: number) {
  if (!baseDate) return "";
  const d = new Date(baseDate);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export function InvoiceFormPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const { data: invoice, isLoading: loadingInvoice } = useInvoice(id ?? "");
  const createMutation = useCreateInvoice();
  const updateMutation = useUpdateInvoice(id ?? "");
  const mutation = isEdit ? updateMutation : createMutation;

  const { data: companiesData } = useCompanies({ active: true, perPage: 200 });
  const companyOptions = (companiesData?.data ?? []).map((c) => ({
    value: c.id,
    label: c.tradeName,
  }));

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<InvoiceFormData>({
    resolver: zodResolver(invoiceSchema),
    defaultValues: {
      companyId: "",
      invoiceNumber: "",
      nfNumber: "",
      nfDate: "",
      totalAmount: "" as unknown as number,
      issueDate: todayISO(),
      dueDate: addDaysISO(todayISO(), 30),
      freightIds: [],
    },
  });

  const { field: companyField, fieldState: { error: companyError } } =
    useController({ control, name: "companyId" });
  const { field: freightIdsField } = useController({ control, name: "freightIds" });

  const companyId = useWatch({ control, name: "companyId" });
  const freightIds = useWatch({ control, name: "freightIds" }) ?? [];
  const issueDate = useWatch({ control, name: "issueDate" });

  // Recalcula vencimento (emissão + 30) enquanto o usuário não tiver editado manualmente
  useEffect(() => {
    if (isEdit) return;
    if (dirtyFields.dueDate) return;
    if (!issueDate) return;
    setValue("dueDate", addDaysISO(issueDate, 30));
  }, [issueDate, dirtyFields.dueDate, isEdit, setValue]);

  // Auto-fill totalAmount from selected freights
  const { data: freightsData } = useFreights(
    companyId ? { companyId, status: "COMPLETED", perPage: 100 } : { status: "COMPLETED", perPage: 100 },
  );

  useEffect(() => {
    if (!isEdit && freightIds.length > 0 && freightsData?.data) {
      const selected = freightsData.data.filter((f) => freightIds.includes(f.id));
      const total = selected.reduce((sum, f) => sum + Number.parseFloat(f.freightValue), 0);
      if (total > 0) {
        setValue("totalAmount", total as unknown as number);
      }
    }
  }, [freightIds, freightsData, isEdit, setValue]);

  useEffect(() => {
    if (invoice) {
      reset({
        companyId: invoice.companyId,
        invoiceNumber: invoice.invoiceNumber,
        nfNumber: invoice.nfNumber ?? "",
        nfDate: toDateInput(invoice.nfDate),
        totalAmount: Number.parseFloat(invoice.totalAmount) as unknown as number,
        issueDate: toDateInput(invoice.issueDate),
        dueDate: toDateInput(invoice.dueDate),
        freightIds: invoice.freights.map((f) => f.id),
      });
    }
  }, [invoice, reset]);

  async function onSubmit(data: InvoiceFormData) {
    try {
      const payload = {
        ...data,
        dueDate: toISOString(data.dueDate),
        issueDate: data.issueDate ? toISOString(data.issueDate) : undefined,
        nfDate: data.nfDate ? toISOString(data.nfDate) : undefined,
      };
      await mutation.mutateAsync(payload as InvoiceFormData);
      toast.success(isEdit ? "Fatura atualizada!" : "Fatura cadastrada!");
      navigate("/invoices");
    } catch (err: unknown) {
      const msg = (
        err as { response?: { data?: { error?: { message?: string } } } }
      )?.response?.data?.error?.message;
      toast.error(msg ?? "Erro ao salvar fatura");
    }
  }

  if (isEdit && loadingInvoice) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate("/invoices")}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? "Editar fatura" : "Nova fatura"}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? invoice?.invoiceNumber : "Preencha os dados da fatura"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Empresa */}
        <FormSection title="Empresa">
          <div className="space-y-1.5">
            <Label>Empresa *</Label>
            <ComboboxSelect
              options={companyOptions}
              value={(companyField.value as string) ?? ""}
              onChange={companyField.onChange}
              placeholder="Selecione a empresa..."
              searchPlaceholder="Buscar empresa..."
              aria-invalid={!!companyError}
            />
            {companyError && (
              <p className="text-xs text-destructive">{companyError.message}</p>
            )}
          </div>
        </FormSection>

        {/* Identificação */}
        <FormSection title="Identificação">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="invoiceNumber">Nº da fatura *</Label>
              <Input
                id="invoiceNumber"
                aria-invalid={!!errors.invoiceNumber}
                {...register("invoiceNumber")}
              />
              {errors.invoiceNumber && (
                <p className="text-xs text-destructive">{errors.invoiceNumber.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nfNumber">Nº da NF</Label>
              <Input id="nfNumber" {...register("nfNumber")} />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="issueDate">Data de emissão</Label>
              <Input id="issueDate" type="date" {...register("issueDate")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nfDate">Data da NF</Label>
              <Input id="nfDate" type="date" {...register("nfDate")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dueDate">Vencimento *</Label>
              <Input
                id="dueDate"
                type="date"
                aria-invalid={!!errors.dueDate}
                {...register("dueDate")}
              />
              {errors.dueDate && (
                <p className="text-xs text-destructive">{errors.dueDate.message}</p>
              )}
            </div>
          </div>
        </FormSection>

        {/* Valor */}
        <FormSection title="Valor">
          <div className="space-y-1.5">
            <Label htmlFor="totalAmount">Total da fatura *</Label>
            <MoneyInput
              id="totalAmount"
              error={!!errors.totalAmount}
              {...register("totalAmount")}
            />
            {errors.totalAmount && (
              <p className="text-xs text-destructive">{errors.totalAmount.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Preenchido automaticamente ao selecionar fretes abaixo.
            </p>
          </div>
        </FormSection>

        {/* Fretes */}
        <FormSection title="Fretes cobertos (opcional)">
          <FreightSelector
            companyId={companyId}
            selectedIds={freightIds as string[]}
            onChange={(ids) => freightIdsField.onChange(ids)}
          />
        </FormSection>

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/invoices")}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            loading={mutation.isPending}
            loadingText="Salvando…"
          >
            {isEdit ? "Salvar alterações" : "Cadastrar fatura"}
          </LoadingButton>
        </div>
      </form>
    </div>
  );
}
